/**
 * ProShip Direct v2 — returns and doorstep QC.
 *
 * Built on the grounded model in data/rvpQc.js, which mirrors the admin module at /admin/rvp-qc.
 * The merchant screen and the ops screen are two views of one object, not a copy — so the checks
 * are ProShip's master parameters, meaning the same thing on every courier, and a merchant never
 * meets a carrier's internal parameter codes.
 *
 * ONE POLICY, EVERY COURIER. QC is configured per merchant and applies to every reverse pickup
 * whoever carries it, which is why this lives in Settings rather than in Courier Setup and needs no
 * account picker. It is the one setup dimension in this module that is not per courier account.
 *
 * WHAT WAS CUT from the screen this is based on, since the brief was to remove unnecessary text: the
 * mode chips and the paragraph explaining that there is nothing to filter by mode (there are no
 * modes here); the setup-scope note; the "one setup, two views" footer naming the admin path, which
 * is a fact about our architecture and not about the merchant's returns; and the long blurbs on the
 * check table and the reason matrix, each down to one line. The screen is the same screen; it just
 * stops explaining itself.
 */
import React, { useState } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableHead, TableRow, Checkbox, TextField,
  Chip, Alert, IconButton, ToggleButtonGroup, ToggleButton, Tooltip,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import withLayout from '../../../hocs/Layout';
import PageTitle from '../../../common/PageTitle';
import { useReturnsQc } from '../data/hooks';
import { MASTER_PARAMS, paramName, QC_TYPES } from '../data/rvpQc';
import {
  pageContainerSx, sectionCardSx, sectionTitleSx, primaryBtnSx, secondaryBtnSx,
} from '../../../styles/patterns';
import { tHeadSx, tCellSx } from '../components/stats';
import { SUBTLE, NAVY, BORDER, WHITE, LIGHT_BG, SUCCESS, PRIMARY_BLUE } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP, RADIUS } from '../../../styles/foundation/spacing';

const smallSx = { fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE, lineHeight: 1.5 };
const strongSx = { fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY };

function ReturnsQc() {
  const { setup, patch, errors, editable } = useReturnsQc();
  const [saved, setSaved] = useState(false);

  const edit = (mutate) => {
    if (!editable) return;
    patch(mutate);
    setSaved(false);
  };

  /**
   * Turning a check off must also clear it from every reason.
   *
   * Otherwise the matrix keeps pointing at a check the agent will never be shown, and the reason
   * silently loses its evidence requirement without anything on screen changing.
   */
  const toggleParam = (id) =>
    edit((d) => {
      const i = d.selected.indexOf(id);
      if (i >= 0) {
        d.selected.splice(i, 1);
        delete d.mandatory[id];
        delete d.checkType[id];
        d.reasons.forEach((r) => delete r.cells[id]);
      } else {
        d.selected.push(id);
        d.mandatory[id] = false;
        d.checkType[id] = 'HARD';
      }
    });

  return (
    <Box sx={pageContainerSx}>
      <PageTitle subTitle='Your return reasons, and what the courier checks at the buyer’s door before accepting the parcel'>
        Returns & QC
      </PageTitle>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: GAP.lg }}>
        {!editable && (
          <Alert
            severity='info'
            icon={<LockOutlinedIcon fontSize='small' />}
            sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}
          >
            Read-only. Ask ProShip ops for edit rights to manage this yourself.
          </Alert>
        )}

        {/* ── How returns are checked ── */}
        <Box sx={sectionCardSx}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: GAP.sm, mb: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography sx={{ ...sectionTitleSx, mb: 0 }}>How returns are checked</Typography>
              <Typography sx={smallSx}>
                One policy for every reverse pickup, on every courier.
              </Typography>
            </Box>
            <Chip
              size='small'
              label={setup.status}
              sx={{
                fontFamily: FONT,
                fontSize: SIZE['4xs'],
                fontWeight: WEIGHT.semibold,
                height: 20,
                bgcolor: `${SUCCESS}14`,
                color: SUCCESS,
              }}
            />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0,1fr))' },
              gap: 1.5,
            }}
          >
            {QC_TYPES.map((t) => {
              const active = setup.type === t.key;
              return (
                <Box
                  key={t.key}
                  role='button'
                  tabIndex={editable ? 0 : -1}
                  onClick={() => edit((d) => { d.type = t.key; })}
                  onKeyDown={(e) =>
                    (e.key === 'Enter' || e.key === ' ') && edit((d) => { d.type = t.key; })
                  }
                  sx={{
                    p: 1.5,
                    borderRadius: RADIUS.md,
                    cursor: editable ? 'pointer' : 'default',
                    border: `1px solid ${active ? PRIMARY_BLUE : BORDER}`,
                    bgcolor: active ? `${PRIMARY_BLUE}0a` : WHITE,
                    opacity: editable ? 1 : 0.75,
                    '&:hover': editable ? { borderColor: PRIMARY_BLUE } : {},
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                    <Box
                      sx={{
                        width: 13,
                        height: 13,
                        borderRadius: '50%',
                        flexShrink: 0,
                        border: `2px solid ${active ? PRIMARY_BLUE : BORDER}`,
                        position: 'relative',
                        '&::after': active
                          ? { content: '""', position: 'absolute', inset: 2, borderRadius: '50%', bgcolor: PRIMARY_BLUE }
                          : {},
                      }}
                    />
                    <Typography sx={strongSx}>{t.label}</Typography>
                  </Box>
                  <Typography sx={smallSx}>{t.blurb}</Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        {setup.type === 'nonqc' ? (
          <Alert severity='warning' sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}>
            Nothing is checked at the buyer&rsquo;s door. Every dispute becomes a conversation after
            the parcel reaches your warehouse, and you carry the write-off.
          </Alert>
        ) : (
          <>
            {/* ── The checks ── */}
            <Box sx={sectionCardSx}>
              <Typography sx={{ ...sectionTitleSx, mb: 0 }}>What the agent checks</Typography>
              <Typography sx={{ ...smallSx, mb: 1 }}>
                <b>Reject pickup</b> fails the QC and the agent leaves without the parcel.{' '}
                <b>Record only</b> notes the finding and lets the pickup proceed.
              </Typography>

              {/*
                Said here rather than in a footnote: this is where a merchant picks checks, and it
                is the moment they might otherwise assume every selection binds on every courier.
              */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 0.75,
                  p: 1,
                  mb: 1.5,
                  borderRadius: RADIUS.md,
                  bgcolor: LIGHT_BG,
                  border: `1px solid ${BORDER}`,
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 15, color: SUBTLE, mt: '2px' }} />
                <Typography sx={smallSx}>
                  Not every courier supports every check. Where a courier cannot perform one, that
                  check is skipped for its pickups — the rest of your QC still runs.
                </Typography>
              </Box>

              <Box sx={{ overflowX: 'auto' }}>
                <Table size='small' sx={{ minWidth: 640 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...tHeadSx, minWidth: 50 }}>Use</TableCell>
                      <TableCell sx={{ ...tHeadSx, minWidth: 260 }}>Check</TableCell>
                      <TableCell sx={{ ...tHeadSx, minWidth: 90 }}>Mandatory</TableCell>
                      <TableCell sx={{ ...tHeadSx, minWidth: 200 }}>On failure</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {MASTER_PARAMS.map((p) => {
                      const on = setup.selected.includes(p.id);
                      return (
                        <TableRow key={p.id} sx={{ bgcolor: on ? 'transparent' : LIGHT_BG }}>
                          <TableCell sx={tCellSx}>
                            <Checkbox
                              size='small'
                              checked={on}
                              disabled={!editable}
                              onChange={() => toggleParam(p.id)}
                              inputProps={{ 'aria-label': `Use ${p.name}` }}
                            />
                          </TableCell>
                          <TableCell sx={{ ...tCellSx, whiteSpace: 'normal' }}>
                            <Typography sx={{ ...strongSx, color: on ? NAVY : SUBTLE }}>{p.name}</Typography>
                            <Typography sx={smallSx}>{p.desc}</Typography>
                          </TableCell>
                          <TableCell sx={tCellSx}>
                            <Checkbox
                              size='small'
                              disabled={!on || !editable}
                              checked={Boolean(setup.mandatory[p.id])}
                              onChange={() => edit((d) => { d.mandatory[p.id] = !d.mandatory[p.id]; })}
                              inputProps={{ 'aria-label': `${p.name} mandatory` }}
                            />
                          </TableCell>
                          <TableCell sx={tCellSx}>
                            {on ? (
                              <ToggleButtonGroup
                                size='small'
                                exclusive
                                value={setup.checkType[p.id] || 'HARD'}
                                disabled={!editable}
                                onChange={(e, v) => v && edit((d) => { d.checkType[p.id] = v; })}
                              >
                                <ToggleButton
                                  value='HARD'
                                  sx={{ fontFamily: FONT, fontSize: SIZE['4xs'], textTransform: 'none', px: 1.25, py: 0.3 }}
                                >
                                  Reject pickup
                                </ToggleButton>
                                <ToggleButton
                                  value='SOFT'
                                  sx={{ fontFamily: FONT, fontSize: SIZE['4xs'], textTransform: 'none', px: 1.25, py: 0.3 }}
                                >
                                  Record only
                                </ToggleButton>
                              </ToggleButtonGroup>
                            ) : (
                              <Typography sx={smallSx}>—</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Box>

            {/* ── Reason matrix ── */}
            {setup.type === 'reason' && (
              <Box sx={sectionCardSx}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: GAP.sm, mb: 1.5, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography sx={{ ...sectionTitleSx, mb: 0 }}>Your return reasons</Typography>
                    <Typography sx={smallSx}>
                      What the buyer picks, and which checks it triggers.
                    </Typography>
                  </Box>
                  {editable && (
                    <Button
                      size='small'
                      startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                      sx={secondaryBtnSx}
                      onClick={() =>
                        edit((d) =>
                          d.reasons.push({ id: `r${d.reasons.length + 1}-${d.reasons.length}`, label: '', cells: {} })
                        )
                      }
                    >
                      Add reason
                    </Button>
                  )}
                </Box>

                {errors.length > 0 && (
                  <Alert severity='error' sx={{ mb: 1.5, fontFamily: FONT, fontSize: SIZE['3xs'] }}>
                    {errors.length} to fix before saving:
                    <Box component='ul' sx={{ pl: 2, mt: 0.5, mb: 0 }}>
                      {errors.map((e, i) => (
                        <Box component='li' key={`${e.index}-${i}`}>
                          Row {e.index + 1}: {e.message}
                        </Box>
                      ))}
                    </Box>
                  </Alert>
                )}

                {setup.selected.length === 0 ? (
                  <Typography sx={smallSx}>
                    Pick at least one check above — a reason with nothing to check gives the agent
                    nothing to do.
                  </Typography>
                ) : (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size='small' sx={{ minWidth: 260 + setup.selected.length * 110 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ ...tHeadSx, minWidth: 240 }}>Return reason</TableCell>
                          {setup.selected.map((id) => (
                            <TableCell key={id} align='center' sx={{ ...tHeadSx, minWidth: 100 }}>
                              <Tooltip arrow title={paramName(id)}>
                                <span>{paramName(id).split(' (')[0]}</span>
                              </Tooltip>
                            </TableCell>
                          ))}
                          {editable && <TableCell sx={{ ...tHeadSx, minWidth: 50 }} />}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {setup.reasons.map((r, idx) => (
                          <TableRow key={r.id}>
                            <TableCell sx={{ ...tCellSx, minWidth: 240 }}>
                              <TextField
                                fullWidth
                                size='small'
                                value={r.label}
                                disabled={!editable}
                                placeholder='What the buyer sees'
                                onChange={(e) =>
                                  edit((d) => {
                                    const row = d.reasons.find((x) => x.id === r.id);
                                    if (row) row.label = e.target.value;
                                  })
                                }
                                InputProps={{ sx: { fontFamily: FONT, fontSize: 12 } }}
                              />
                            </TableCell>
                            {setup.selected.map((id) => (
                              <TableCell key={id} align='center' sx={tCellSx}>
                                <Checkbox
                                  size='small'
                                  disabled={!editable}
                                  checked={Boolean(r.cells[id])}
                                  inputProps={{ 'aria-label': `${paramName(id)} for reason ${idx + 1}` }}
                                  onChange={() =>
                                    edit((d) => {
                                      const row = d.reasons.find((x) => x.id === r.id);
                                      if (row) row.cells[id] = !row.cells[id];
                                    })
                                  }
                                />
                              </TableCell>
                            ))}
                            {editable && (
                              <TableCell sx={tCellSx}>
                                <IconButton
                                  size='small'
                                  aria-label={`Remove reason ${idx + 1}`}
                                  onClick={() =>
                                    edit((d) => {
                                      d.reasons = d.reasons.filter((x) => x.id !== r.id);
                                    })
                                  }
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: 16, color: SUBTLE }} />
                                </IconButton>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Box>
            )}
          </>
        )}

        {/* ── Save ── */}
        <Box sx={sectionCardSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.md, flexWrap: 'wrap' }}>
            <Button
              variant='contained'
              size='small'
              sx={primaryBtnSx}
              disabled={!editable || errors.length > 0}
              onClick={() => setSaved(true)}
            >
              Save
            </Button>
            <Typography sx={smallSx}>
              Applies to pickups created from now on. Pickups already scheduled keep the setup they
              were created under.
            </Typography>
          </Box>
          {saved && (
            <Alert severity='success' sx={{ mt: 1.5, fontFamily: FONT, fontSize: SIZE['2xs'] }}>
              Saved.{' '}
              {setup.type === 'reason'
                ? `${setup.reasons.length} reasons across ${setup.selected.length} checks.`
                : `${setup.selected.length} checks on every pickup.`}
            </Alert>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default withLayout(ReturnsQc);
